import React, { Component } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import Autosuggest from 'msyn-autosuggest';
import uuid from 'uuid';
import Loading from 'components/elements/common/loading/Loading';
import { setSuggestions } from 'store/actions/autocomplete';

import { AUTOCOMPLETE_DEBOUNCE_DELAY, MIN_AUTOCOMPLETE_LEN } from '../../utils/constants';
import './Autocomplete.scss';

export const getSectionSuggestions = ({ context, displayName, items }) => items.map(item => ({ ...item, context, displayName }));
export const getSuggestionValue = suggestion => suggestion.label;

export const renderSectionTitle = section => (<strong>{section.displayName}</strong>);

export const renderSuggestion = ({ label, extra }) => {
  const suggestion = label + (extra ? ` - ${extra}` : '');
  return <span className="search--suggestion">{suggestion}</span>;
};
export class Autocomplete extends Component {
  constructor() {
    super();

    this.state = {
      query: '',
      loading: false,
    };

    this.onChange = this.onChange.bind(this);
    this.loadSuggestions = this.loadSuggestions.bind(this);
    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.shouldRenderSuggestions = this.shouldRenderSuggestions.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);

    this.debounceLoadSuggestions = debounce(this.loadSuggestions, AUTOCOMPLETE_DEBOUNCE_DELAY);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searching !== this.props.searching) {
      this.setState({ loading: false });
    }
  }

  onChange(event, { newValue }) {
    this.setState({
      query: newValue,
      loading: !!newValue.length,
    });
    this.props.onChange(event, newValue);
  }

  /* istanbul ignore next */ // no need to test lib
  loadSuggestions(query) {
    const { getSuggestions, autocompleteContexts } = this.props;
    getSuggestions(query, autocompleteContexts);
  }

  onSuggestionsFetchRequested({ value: query }) {
    this.debounceLoadSuggestions(query);
  }

  /* istanbul ignore next */ // there is no clear button shown
  onSuggestionsClearRequested() {
    this.setState({ loading: false });
    /* istanbul ignore next */
    if (!this.state.query) {
      this.props.clearSuggestions();
    }
  }

  shouldRenderSuggestions(query) {
    return query && query.trim().length >= MIN_AUTOCOMPLETE_LEN;
  }

  onSuggestionSelected(event, { suggestion }) {
    event.preventDefault();

    const { context, id: value, label, extra } = suggestion;
    this.setState({
      query: `${context}: ${suggestion.label}`,
    });
    const displayValue = label + (extra ? ` - ${extra}` : '');
    this.props.onSelect({ context, value, displayValue, autocomplete: true });
  }

  render() {
    const { query, loading } = this.state;
    const { id = uuid.v4(), ariaLabel, suggestions, placeholder = '', value = false } = this.props;

    let inputValue = value;
    // value can be an empty string for searches w/o queries
    if (inputValue === false) {
      inputValue = query;
    }
    const inputProps = {
      placeholder,
      'aria-label': id,
      value: inputValue,
      onChange: this.onChange,
    };

    return (
      <div className="autocomplete">
        <Autosuggest
          id={id}
          ariaLabel={ariaLabel}
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          shouldRenderSuggestions={this.shouldRenderSuggestions}
          onSuggestionSelected={this.onSuggestionSelected}
          getSectionSuggestions={getSectionSuggestions}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          renderSectionTitle={renderSectionTitle}
          inputProps={inputProps}
          multiSection={true}
        />
        {loading && <Loading height={18} width={50}/>}
      </div>
    );
  }
}

const mapStateToProps = ({ autocomplete }) => ({
  suggestions: autocomplete.suggestions,
  searching: autocomplete.loading,
});

/* istanbul ignore next */ // no need to test lib
const mapDispatchToProps = dispatch => ({
  clearSuggestions: () => dispatch(setSuggestions([])),
});

export default connect(mapStateToProps, mapDispatchToProps)(Autocomplete);
